import * as Three from 'three';
import { LoadingManager } from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import toast from 'react-hot-toast';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import { setLoadingMessage } from '../../redux/Slice/globalStates';
import {
  add_new_assets,
  update_assets_data_new,
  addModel,
  addSceneMesh,
  selected_object,
  set_is_glb_image_editoropen,
  setPushIntersectedObject,
} from '../../redux/Slice/gridSlice';
import store from '../../redux/store';
import { getNextCopyName, buildTempFileName } from '../../utils/NamingUtils';
import { copyPhysicsToDuplicate } from '../../utils/Physics/PhysicsUtils';
import { UpdateMaterial, normalizeImportedMeshMaterialsForExport } from './materialFunction';
import { handleMeshUpload } from './Mesh';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';
import { isolateDuplicatePublishIdentity } from '../../pages/Training/utils/gridpageUtils';

// ──────────────────────────────────────────────────────────────
// FOLDER STRUCTURE: resolve FBX under model/source, textures under texture
// ──────────────────────────────────────────────────────────────

const MODEL_PATH_SEGMENTS = ['model', 'source', 'models'];
const TEXTURE_PATH_SEGMENTS = ['texture', 'textures', 'tex', 'maps', 'materials'];

function getPath(file) {
  const p = file.webkitRelativePath || file.name || '';
  return p.replace(/\\/g, '/').toLowerCase();
}

function pathContainsSegment(path, segments) {
  if (!path) return false;
  const lower = String(path).toLowerCase();
  return segments.some((seg) => lower.includes(`/${seg}/`) || lower.startsWith(`${seg}/`));
}

/** Prefer FBX under model/ or source/ (nested), else first .fbx in list */
function findFbxFile(files) {
  const fbxFiles = files.filter((f) => f.name && f.name.toLowerCase().endsWith('.fbx'));
  if (!fbxFiles.length) return null;
  const underModel = fbxFiles.find((f) => pathContainsSegment(getPath(f), MODEL_PATH_SEGMENTS));
  return underModel || fbxFiles[0];
}

const IMAGE_EXTS = /\.(png|jpg|jpeg|bmp|gif|tga|tif|tiff|webp)$/i;

/** All image files in the folder (for texture resolution). Prefer texture subfolders first. */
function getTextureFiles(files) {
  const images = files.filter((f) => f.name && IMAGE_EXTS.test(f.name));
  const underTexture = images.filter((f) => pathContainsSegment(getPath(f), TEXTURE_PATH_SEGMENTS));
  const rest = images.filter((f) => !underTexture.includes(f));
  return [...underTexture, ...rest];
}

/** Build path -> blob URL map so FBXLoader can resolve texture paths (e.g. "diffuse.png", "Texture/diffuse.png"). */
function buildTexturePathToBlobUrl(textureFiles) {
  const pathToBlobUrl = {};
  const blobUrlsToRevoke = [];
  for (const file of textureFiles) {
    const rel = (file.webkitRelativePath || file.name || '').replace(/\\/g, '/');
    const blobUrl = URL.createObjectURL(file);
    blobUrlsToRevoke.push(blobUrl);
    for (const k of getAllKeys(rel)) {
      pathToBlobUrl[k] = blobUrl;
      pathToBlobUrl[k.toLowerCase()] = blobUrl;
    }
    pathToBlobUrl[file.name] = blobUrl;
    pathToBlobUrl[file.name.toLowerCase()] = blobUrl;
  }
  return { pathToBlobUrl, blobUrlsToRevoke };
}

// ──────────────────────────────────────────────────────────────
// TEXTURE LIB (keyed by path/filename for FBX material resolution)
// ──────────────────────────────────────────────────────────────

function getAllKeys(pathStr) {
  if (!pathStr) return [];
  const norm = String(pathStr).replace(/\\/g, '/').trim();
  const parts = norm.split('/');
  const filename = parts[parts.length - 1];
  const basename = filename.replace(/\.[^/.]+$/, '');
  const keys = new Set();
  for (let i = 0; i < parts.length; i++) {
    const suffix = parts.slice(i).join('/');
    keys.add(suffix);
    keys.add(suffix.toLowerCase());
  }
  keys.add(filename);
  keys.add(filename.toLowerCase());
  keys.add(basename);
  keys.add(basename.toLowerCase());
  return Array.from(keys).filter(Boolean);
}

function storeTexture(materialLib, pathStr, tex) {
  for (const k of getAllKeys(pathStr)) {
    materialLib[k] = tex;
  }
}

function findTexture(materialLib, ref) {
  if (!ref) return null;
  for (const k of getAllKeys(ref)) {
    if (materialLib[k]) return materialLib[k];
  }
  return null;
}

function loadTextureFromFile(materialLib, file, pathStr, renderer) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      new Three.TextureLoader().load(
        e.target.result,
        (tex) => {
          tex.wrapS = Three.RepeatWrapping;
          tex.wrapT = Three.RepeatWrapping;
          tex.minFilter = Three.LinearMipMapLinearFilter;
          tex.magFilter = Three.LinearFilter;
          if (renderer && renderer.capabilities) {
            tex.anisotropy = renderer.capabilities.getMaxAnisotropy?.() ?? 1;
          }
          tex.colorSpace = Three.SRGBColorSpace;
          tex.flipY = true;
          tex.needsUpdate = true;
          storeTexture(materialLib, pathStr, tex);
          resolve(tex);
        },
        undefined,
        reject
      );
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function buildFbxTextureLib(textureFiles, renderer) {
  const materialLib = {};
  for (const file of textureFiles) {
    const rel = file.webkitRelativePath || file.name;
    try {
      await loadTextureFromFile(materialLib, file, rel, renderer);
    } catch (_) {
      console.warn(`[FbxFunction] Failed to load texture: ${file.name}`);
    }
  }
  return materialLib;
}

/** Categorize texture keys by type (diffuse, normal, roughness, etc.) - returns arrays of texture objects (unique). */
function categorizeTextures(materialLib) {
  const keys = Object.keys(materialLib);
  const seen = new Set();
  const diffuse = [];
  const normal = [];
  const roughness = [];
  const metalness = [];
  const ao = [];
  const emissive = [];

  const isNormal = (k) => /normal|_n\.|_n$|nrm|_nor\./i.test(k);
  const isRoughness = (k) => /rough|_r\.|_r$|roughness/i.test(k);
  const isMetalness = (k) => /metal|_m\.|_m$|metallic/i.test(k);
  const isAO = (k) => /ao|ambient|_ao\.|occlusion/i.test(k);
  const isEmissive = (k) => /emiss|emit|_e\./i.test(k);
  const isDiffuse = (k) =>
    !isNormal(k) &&
    !isRoughness(k) &&
    !isMetalness(k) &&
    !isAO(k) &&
    !isEmissive(k) &&
    (/diffuse|albedo|basecolor|color|_d\.|_c\.|base_colour|_col\./i.test(k) ||
      /\.(png|jpg|jpeg|bmp|tga|tif|webp)$/i.test(k));

  for (const k of keys) {
    const tex = materialLib[k];
    if (!tex) continue;
    const kl = k.toLowerCase();
    if (isNormal(kl)) {
      if (!seen.has(tex)) {
        normal.push(tex);
        seen.add(tex);
      }
    } else if (isRoughness(kl)) {
      if (!seen.has(tex)) {
        roughness.push(tex);
        seen.add(tex);
      }
    } else if (isMetalness(kl)) {
      if (!seen.has(tex)) {
        metalness.push(tex);
        seen.add(tex);
      }
    } else if (isAO(kl)) {
      if (!seen.has(tex)) {
        ao.push(tex);
        seen.add(tex);
      }
    } else if (isEmissive(kl)) {
      if (!seen.has(tex)) {
        emissive.push(tex);
        seen.add(tex);
      }
    } else if (isDiffuse(kl) || /\.(png|jpg|jpeg|bmp|tga|tif|webp)$/i.test(kl)) {
      if (!seen.has(tex)) {
        diffuse.push(tex);
        seen.add(tex);
      }
    }
  }
  if (diffuse.length === 0) {
    for (const k of keys) {
      const tex = materialLib[k];
      if (tex && !seen.has(tex)) {
        diffuse.push(tex);
        seen.add(tex);
      }
    }
  }
  return { diffuse, normal, roughness, metalness, ao, emissive };
}

/** Collect all unique materials from the model with optional mesh name for texture matching. */
function collectMaterials(model) {
  const materials = [];
  const seen = new Set();
  model.traverse((child) => {
    if (!child.isMesh || !child.material) return;
    const meshName = child.name || '';
    const mats = Array.isArray(child.material) ? child.material : [child.material];
    mats.forEach((mat) => {
      if (mat && !seen.has(mat)) {
        seen.add(mat);
        materials.push({ mat, meshName });
      }
    });
  });
  return materials;
}

/** Try to pick a texture whose key matches material or mesh name (any key in lib that points to tex). */
function findTextureByMaterialName(materialLib, matName, meshName, preferType = 'diffuse') {
  const target = (matName || meshName || '').toLowerCase().replace(/\s+/g, '_');
  const keys = Object.keys(materialLib);
  for (const k of keys) {
    const kl = k.toLowerCase().replace(/\.[^/.]+$/, '');
    if (target && (kl.includes(target) || target.includes(kl))) return materialLib[k];
  }
  return null;
}

/** Apply ALL textures from lib to FBX materials: by name match, then by index so every material gets a texture. */
function applyTexturesToFbxModel(model, materialLib) {
  const keys = Object.keys(materialLib);
  if (!keys.length) return;

  const { diffuse, normal, roughness, metalness, ao, emissive } = categorizeTextures(materialLib);
  const materials = collectMaterials(model);
  let diffuseIdx = 0;
  let normalIdx = 0;
  let roughIdx = 0;
  let metalIdx = 0;
  let aoIdx = 0;
  let emissiveIdx = 0;

  materials.forEach(({ mat, meshName }) => {
    if (!mat) return;
    const matName = mat.name || '';

    let mapTex =
      findTexture(materialLib, matName) ||
      findTextureByMaterialName(materialLib, matName, meshName) ||
      findTexture(materialLib, matName.replace(/\.[^.]+$/, '')) ||
      findTexture(materialLib, meshName) ||
      findTextureByMaterialName(materialLib, meshName, matName) ||
      findTexture(materialLib, 'diffuse') ||
      findTexture(materialLib, 'albedo') ||
      findTexture(materialLib, 'basecolor');
    if (!mapTex && diffuse.length > 0) mapTex = diffuse[diffuseIdx++ % diffuse.length];
    if (!mapTex) mapTex = materialLib[keys[0]];

    let normalTex = findTexture(materialLib, matName + '_normal') || findTexture(materialLib, matName + '_n') || findTexture(materialLib, meshName + '_normal');
    if (!normalTex && normal.length > 0) normalTex = normal[normalIdx++ % normal.length];

    let roughTex = findTexture(materialLib, matName + '_roughness') || findTexture(materialLib, matName + '_r');
    if (!roughTex && roughness.length > 0) roughTex = roughness[roughIdx++ % roughness.length];

    let metalTex = findTexture(materialLib, matName + '_metalness') || findTexture(materialLib, matName + '_m');
    if (!metalTex && metalness.length > 0) metalTex = metalness[metalIdx++ % metalness.length];

    let aoTex = findTexture(materialLib, matName + '_ao') || findTexture(materialLib, matName + '_ambient');
    if (!aoTex && ao.length > 0) aoTex = ao[aoIdx++ % ao.length];

    let emissiveTex = findTexture(materialLib, matName + '_emissive') || findTexture(materialLib, matName + '_e');
    if (!emissiveTex && emissive.length > 0) emissiveTex = emissive[emissiveIdx++ % emissive.length];

    if (mapTex) {
      mat.map = mapTex;
      mat.color = new Three.Color(0xffffff);
    }
    if (normalTex) mat.normalMap = normalTex;
    if (roughTex) mat.roughnessMap = roughTex;
    if (metalTex) mat.metalnessMap = metalTex;
    if (aoTex) mat.aoMap = aoTex;
    if (emissiveTex) mat.emissiveMap = emissiveTex;
    mat.needsUpdate = true;
    preserveFbxMaterial(mat);
  });
}

function preserveFbxMaterial(material) {
  if (!material) return;
  if (material.map) material.map.needsUpdate = true;
  if (material.normalMap) material.normalMap.needsUpdate = true;
  if (!material.userData.material_settings) {
    material.userData.material_settings = {
      albedo: material.color ? `#${material.color.getHexString()}` : '#FFFFFF',
      metallicType: (material.metalness ?? 0) > 0.01,
      emissionType: false,
      renderingMode: 'Opaque',
      added: true,
    };
  }
}

function finishAddToScene(model, file, objectURL, tempId, setPendingUploads, scene, transformControls, setPolyCount) {
  model.rotation.set(0, 0, 0);
  model.name = tempId;
  model.objPath = file;
  model.objType = '3d';
  model._id = tempId;
  if (setPendingUploads) {
    setPendingUploads((prev) => [
      ...prev,
      {
        file,
        transform: {
          position_x: model.position.x || 0,
          position_y: model.position.y || 0,
          position_z: model.position.z || 0,
          rotation_x: model.rotation?.x || 0,
          rotation_y: model.rotation?.y || 0,
          rotation_z: model.rotation?.z || 0,
          scale_x: model.scale?.x || 1,
          scale_y: model.scale?.y || 1,
          scale_z: model.scale?.z || 1,
        },
        tempId,
      },
    ]);
  }
  model.glb_type = 'fbx';
  model.originalFile = file;
  model.file_name = file.name;
  model.url = tempId;
  model.deleteType = 'mesh';
  store.dispatch(setPushIntersectedObject(model));
  scene.add(model);
  setPolyCount((prev) => prev + 1);
  store.dispatch(add_new_assets({ type: 'mesh', data: model }));
  store.dispatch(set_is_glb_image_editoropen(true));
  if (transformControls) transformControls.attach(model);
  selectObjectInScene(tempId);
  store.dispatch(addModel({ url: objectURL, name: file.name }));
  store.dispatch(addSceneMesh(model));
  store.dispatch(selected_object(model));
  store.dispatch(setLoadingMessage(false));
  toast.success(`FBX model "${file.name}" loaded successfully!`);
  URL.revokeObjectURL(objectURL);
}

// ──────────────────────────────────────────────────────────────
// UPLOAD: folder (model + texture) or single file
// ──────────────────────────────────────────────────────────────

export function handleMeshFbxUpload(
  e,
  {
    scene,
    transformControls,
    selectedObject,
    setPolyCount,
    renderer,
    GLTFLoader,
    DRACOLoader
  },
  setPendingUploads
) {
  const files = Array.from(e.target.files || []);
  if (!files.length) return;

  const fbxFile = findFbxFile(files);
  if (!fbxFile) {
    toast.error('No FBX file found. For folder upload, place the .fbx under a "model" or "source" folder.');
    return;
  }

  const maxSize = 100 * 1024 * 1024; // 50MB
  if (fbxFile.size > maxSize) {
    toast.error('FBX file size should not exceed 100MB.', {
      duration: 3000,
      style: { background: '#1d1e21', color: '#fff' },
    });
    return;
  }

  selectedObject = null;
  store.dispatch(selected_object(null));
  store.dispatch(setLoadingMessage(true));

  const objectURL = URL.createObjectURL(fbxFile);
  const tempId = `temp-${Date.now()}-${fbxFile.name}`;
  const textureFiles = getTextureFiles(files);
  const { pathToBlobUrl, blobUrlsToRevoke } = buildTexturePathToBlobUrl(textureFiles);

  const manager = new LoadingManager();
  manager.setURLModifier((url) => {
    if (!url || typeof url !== 'string') return url;
    const normalized = url.replace(/\\/g, '/');
    const parts = normalized.split('/');
    const filename = decodeURIComponent(parts[parts.length - 1] || '');
    const filenameLower = filename.toLowerCase();
    if (pathToBlobUrl[filename]) return pathToBlobUrl[filename];
    if (pathToBlobUrl[filenameLower]) return pathToBlobUrl[filenameLower];
    if (pathToBlobUrl[normalized]) return pathToBlobUrl[normalized];
    if (pathToBlobUrl[normalized.toLowerCase()]) return pathToBlobUrl[normalized.toLowerCase()];
    const lastSegment = normalized.slice(normalized.lastIndexOf('/') + 1);
    if (pathToBlobUrl[lastSegment]) return pathToBlobUrl[lastSegment];
    if (pathToBlobUrl[lastSegment.toLowerCase()]) return pathToBlobUrl[lastSegment.toLowerCase()];
    return url;
  });

  const loader = new FBXLoader(manager);
  const onLoaded = (model) => {
    blobUrlsToRevoke.forEach((url) => URL.revokeObjectURL(url));
    if (textureFiles.length > 0) {
      buildFbxTextureLib(textureFiles, renderer).then((materialLib) => {
        applyTexturesToFbxModel(model, materialLib);
        convertToGLBandLoad(model, fbxFile);
      });
    } else {
      convertToGLBandLoad(model, fbxFile);
    }
  };

  const convertToGLBandLoad = (model, fbxFile) => {
    store.dispatch(setLoadingMessage(true)); // Keep loading true during export
    normalizeImportedMeshMaterialsForExport(model);
    const exporter = new GLTFExporter();

    const exportOptions = { binary: true };
    if (model.animations && model.animations.length > 0) {
      exportOptions.animations = model.animations;
    }

    exporter.parse(
      model,
      (gltf) => {
        const blob = new Blob([gltf], { type: 'model/gltf-binary' });
        const glbFile = new File([blob], fbxFile.name.replace(/\.[^/.]+$/, '.glb'), { type: 'model/gltf-binary' });

        // Pass the new glbFile to handleMeshUpload
        handleMeshUpload(
          { target: { files: [glbFile] } },
          {
            GLTFLoader,
            DRACOLoader,
            scene,
            transformControls,
            selectedObject,
            setPolyCount,
          },
          setPendingUploads
        );
      },
      (error) => {
        console.error('GLTF Export failed', error);
        toast.error('Failed to convert FBX to GLB.');
        store.dispatch(setLoadingMessage(false));
      },
      exportOptions
    );
  };

  loader.load(
    objectURL,
    onLoaded,
    function (xhr) {
      if (xhr.lengthComputable) {
        const percent = (xhr.loaded / xhr.total) * 100;
      }
    },
    function (error) {
      console.error('Failed to load FBX model:', error);
      blobUrlsToRevoke.forEach((url) => URL.revokeObjectURL(url));
      store.dispatch(setLoadingMessage(false));
      URL.revokeObjectURL(objectURL);
      toast.error('Failed to load FBX model.');
    }
  );
}

export function DownloadMeshFbx(
  meshData,
  { Three, scene, selectedObject, setPolyCount }
) {
  meshData.forEach((mesh) => {
    const meshPath = mesh.asset_file_path;
    const position_x = mesh.position_x;
    const position_y = mesh.position_y;
    const position_z = mesh.position_z;
    const rotation_x = mesh.rotation_x;
    const rotation_y = mesh.rotation_y;
    const rotation_z = mesh.rotation_z;
    const scale_x = mesh.scale_x;
    const scale_y = mesh.scale_y;
    const scale_z = mesh.scale_z;

    const loader = new FBXLoader();
    const objectURL = meshPath;
    store.dispatch(addModel({ url: objectURL, name: mesh.asset_file_path }));

    loader.load(
      objectURL,
      function (model) {
        const box = new Three.Box3().setFromObject(model);
        const center = new Three.Vector3();
        box.getCenter(center);
        model.traverse((child) => {
          if (child.isMesh && child.geometry) {
            child.geometry.translate(-center.x, -center.y, -center.z);
          }
        });

        model.name = mesh._id;
        model.url = mesh._id;
        model.deleteType = 'mesh';
        model.glb_type = 'fbx';
        model.rotation.eulerOrder = 'XYZ';
        model.rotation.order = 'XYZ';

        model.position.set(position_x, position_y, position_z);
        model.rotation.set(
          Three.MathUtils.degToRad(rotation_x),
          Three.MathUtils.degToRad(rotation_y),
          Three.MathUtils.degToRad(rotation_z)
        );
        model.scale.set(scale_x, scale_y, scale_z);
        model.updateMatrix();
        model.updateMatrixWorld(true);

        model.userData = {};
        model.userData.material_settings = mesh.material_settings;
        model.objType = '3d';

        const boundingBox = new Three.Box3().setFromObject(model);
        const size = new Three.Vector3();
        boundingBox.getSize(size);

        model.traverse(function (child) {
          if (child.isMesh) {
            child.updateMatrixWorld(true);
            child.castShadow = true;
            child.receiveShadow = true;

            if (child.material) {
              child.material.visible = true;
              child.material.needsUpdate = true;

              const mats = Array.isArray(child.material)
                ? child.material
                : [child.material];
              mats.forEach((mat) => {
                if (!mat) return;
                const opacity =
                  typeof mat.opacity === 'number' ? mat.opacity : 1;
                // Don't keep near-opaque FBX materials as transparent (looks like Cutout).
                if (opacity >= 0.999) {
                  mat.transparent = false;
                  mat.opacity = 1;
                  mat.alphaTest = 0;
                  if (mat.depthWrite !== undefined) mat.depthWrite = true;
                } else if (mat.transparent) {
                  mat.depthWrite = false;
                }
              });

              child.material.side = Three.DoubleSide;
            }
          }
        });

        model.events = mesh.events;
        scene.add(model);
        selectedObject = model;

        setPolyCount((prevCount) => prevCount + 1);
        store.dispatch(setPushIntersectedObject(model));
        selectObjectInScene(mesh._id);
        store.dispatch(selected_object(model));
        store.dispatch(addSceneMesh(model));

        UpdateMaterial({ object: model, addData: mesh.material_settings });
      },
      function (xhr) { },
      function (error) {
        console.error('Error loading FBX model:', error);
      }
    );
  });
}



export async function duplicateMeshFbxAsset(
  item,
  { Three, scene, transformControls, selectedObject },
  setPendingUploads,
  originalMesh = null
) {
  if (!item || !item.asset_file_path || !item.file_name) {
    toast.error(
      'Valid item with file path and name are required for duplication'
    );
    return;
  }

  selectedObject = null;
  store.dispatch(selected_object(null));
  transformControls?.detach();

  store.dispatch(setLoadingMessage(true));

  const baseName = item.file_name.replace(/\.[^/.]+$/, ''); // strip extension
  const duplicateFileName =
    getNextCopyName(scene, baseName) +
    (item.file_name.match(/\.[^/.]+$/)?.[0] || '');

  const tempId = buildTempFileName(duplicateFileName);
  const duplicateMeshCode = `mesh_${Math.random().toString(36).substr(2, 6)}`;
  const loader = new FBXLoader();

  loader.load(
    item.asset_file_path,
    function (duplicatedMesh) {
      // Compute and center the bounding box before adding to the scene
      const box = new Three.Box3().setFromObject(duplicatedMesh);
      const center = new Three.Vector3();
      box.getCenter(center);
      duplicatedMesh.traverse((child) => {
        if (child.isMesh && child.geometry) {
          child.geometry.translate(-center.x, -center.y, -center.z);
        }
      });

      // Set basic properties
      duplicatedMesh.name = tempId;
      duplicatedMesh.url = tempId;
      duplicatedMesh._id = tempId;
      duplicatedMesh.temp_file_name = tempId;
      duplicatedMesh.file_name = duplicateFileName;
      duplicatedMesh.deleteType = 'mesh';
      duplicatedMesh.glb_type = 'fbx';
      isolateDuplicatePublishIdentity(duplicatedMesh);

      // Apply transform properties from item with slight offset to avoid overlap
      duplicatedMesh.position.set(
        item.position_x,
        item.position_y,
        item.position_z
      );

      duplicatedMesh.rotation.set(
        Three.MathUtils.degToRad(item.rotation_x || 0),
        Three.MathUtils.degToRad(item.rotation_y || 0),
        Three.MathUtils.degToRad(item.rotation_z || 0)
      );

      duplicatedMesh.scale.set(
        item.scale_x || 1,
        item.scale_y || 1,
        item.scale_z || 1
      );

      // Store all item properties in userData
      duplicatedMesh.userData = {
        material_settings: { ...(item.material_settings || {}) },
        project: item.project,
        user: item.user,
        asset_file_name: item.asset_file_name,
        file_name: duplicateFileName,
        asset_file_type: item.asset_file_type,
        asset_thumbnail: item.asset_thumbnail,
        position_x: duplicatedMesh.position.x,
        position_y: duplicatedMesh.position.y,
        position_z: duplicatedMesh.position.z,
        scale_x: duplicatedMesh.scale.x,
        scale_y: duplicatedMesh.scale.y,
        scale_z: duplicatedMesh.scale.z,
        rotation_x: item.rotation_x || 0,
        rotation_y: item.rotation_y || 0,
        rotation_z: item.rotation_z || 0,
        events: [...(item.events || [])],
        saved: false,
        mesh_code: duplicateMeshCode,
        originalId: item._id || item.id,
        isDuplicate: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Handle pending uploads if needed
      if (setPendingUploads && originalMesh?.originalFile) {
        setPendingUploads((prev) => [
          ...prev,
          {
            file: originalMesh.originalFile,
            transform: {
              position_x: duplicatedMesh.position.x,
              position_y: duplicatedMesh.position.y,
              position_z: duplicatedMesh.position.z,
              rotation_x: item.rotation_x || 0,
              rotation_y: item.rotation_y || 0,
              rotation_z: item.rotation_z || 0,
              scale_x: duplicatedMesh.scale.x,
              scale_y: duplicatedMesh.scale.y,
              scale_z: duplicatedMesh.scale.z,
            },
            tempId: tempId,
            itemData: duplicatedMesh.userData,
          },
        ]);
      }

      // Add to scene
      scene.add(duplicatedMesh);

      // Update Redux store
      store.dispatch(setPushIntersectedObject(duplicatedMesh));
      store.dispatch(addSceneMesh(duplicatedMesh));
      store.dispatch(
        addModel({ url: item.asset_file_path, name: duplicateFileName })
      );
      copyPhysicsToDuplicate(originalMesh || item, duplicatedMesh);

      const displayName =
        tempId.length > 30 ? `${tempId.substring(0, 30)}...` : tempId;
      toast.success(`FBX model duplicated as "${displayName}"`);

      // Update Redux store with new asset
      const duplicateItemData = {
        ...item,
        _id: tempId,
        id: tempId,
        temp_file_name: tempId,
        url: tempId,
        file_name: duplicateFileName,
        mesh_code: duplicateMeshCode,
        position_x: duplicatedMesh.position.x,
        position_y: duplicatedMesh.position.y,
        position_z: duplicatedMesh.position.z,
        parent: undefined,
        saved: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        originalId: item._id || item.id,
        isDuplicate: true,
      };

      try {
        store.dispatch(
          add_new_assets({
            type: 'mesh',
            data: duplicatedMesh,
            itemData: duplicateItemData,
          })
        );
      } catch (err) {
        console.warn(
          'Failed to update assets_data immediately for duplicate:',
          err
        );
      }

      store.dispatch(set_is_glb_image_editoropen(true));
      store.dispatch(selected_object(duplicatedMesh));
      store.dispatch(setLoadingMessage(false));
    },
    undefined,
    function (error) {
      console.error('Error duplicating FBX:', error);
      toast.error('Failed to duplicate FBX asset');
      store.dispatch(setLoadingMessage(false));
    }
  );
}